/**
 * Resolve the shop subdomain for the public customer booking page.
 *
 * Hybrid resolution (in order):
 *  1. Subdomain on the actual hostname (e.g. `sharpandsons.instacut.co.uk`)
 *     → use `sharpandsons`. Ignores known non-shop hostnames (localhost,
 *     `*.preview.emergentagent.com`, plain `instacut.co.uk`, `www`).
 *  2. Path parameter `/book/<subdomain>` (handled by react-router).
 *  3. Query string `?shop=<subdomain>`.
 *
 * Returns the subdomain string, or null if nothing matched.
 */

// One-line rebrand seam. If we ever change the public base domain,
// flipping `REACT_APP_BOOKING_BASE_DOMAIN` in .env swaps every
// user-facing "<sub>.<domain>" label across the admin surface AND
// this resolver's hostname parser without further code edits.
export const BOOKING_BASE_DOMAIN =
  process.env.REACT_APP_BOOKING_BASE_DOMAIN || 'instacut.co.uk';

const BASE = BOOKING_BASE_DOMAIN.toLowerCase();
const NON_SHOP_HOSTS = new Set([
  'localhost', '127.0.0.1', BASE, `www.${BASE}`,
]);

export const resolveSubdomainFromUrl = ({ hostname, pathname, search, pathSubdomain } = {}) => {
  // 1) explicit react-router param wins
  if (pathSubdomain) return pathSubdomain.toLowerCase();

  // 2) hostname-based
  if (hostname) {
    const host = hostname.toLowerCase();
    if (!NON_SHOP_HOSTS.has(host)) {
      // skip preview hosts like `instacut-mockups.preview.emergentagent.com`
      const isPreview = host.endsWith('.preview.emergentagent.com');
      if (!isPreview) {
        // For <BASE> → first label is the shop subdomain
        if (host.endsWith(`.${BASE}`)) {
          const sub = host.replace(`.${BASE}`, '');
          if (sub && sub !== 'www') return sub;
        }
      }
    }
  }

  // 3) query string fallback
  if (search) {
    const usp = new URLSearchParams(search);
    const q = usp.get('shop');
    if (q) return q.toLowerCase();
  }

  return null;
};
